import { FC } from "react";
import styled, { ThemeContext } from "styled-components";
import { color, color as themeColor, fonts } from "@styles";
import { H1 } from "../atoms/Heading";
import { getStrapiImageByBreakpoint } from "@utils";
import { Meta } from "components/atoms/Meta";
import { Cookie } from "components/atoms/svgs/Cookie";
import { LegalDataQuery } from "gql";
import { YuLifeLocale } from "@hooks";
import { useLocaleData } from "hooks/useLocaleData";
import data from "components/organisms/CookiesBar/translations";

export type ContentType = "article" | "seo-page";

const giveBlockQuotesCurlyQuotationMarks = (str: string): string => {
  return (str || "")
    .replace(`<blockquote>"`, "<blockquote>“")
    .replace(`"</blockquote>`, "”</blockquote>");
};

const makeAnchorsOpenNewTab = (str: string): string => {
  return (str || "").replace(
    /<a (?=href="[^\"]*">)/g,
    `<a target="_blank" rel="noopener noreferrer"`,
  );
};

const LegalPage: FC<
  React.PropsWithChildren<{
    postData?: LegalDataQuery["legalPages"]["data"][number]["attributes"];
    contentType?: ContentType;
    locale: YuLifeLocale;
  }>
> = ({ postData, locale }) => {
  if (postData) {
    const { title, excerpt, featuredImage, metaTitle, metaDescription, slug, post } = postData;
    const { cookiesLegalPage } = useLocaleData(data);

    const isCookiePolicy = slug === "cookie-policy";

    const hasYuLifeSecondaryTitle = /\|\sYuLife/i.test(metaTitle);

    const metadata = {
      description: metaDescription || excerpt,
      image: featuredImage.data?.attributes?.formats
        ? getStrapiImageByBreakpoint(featuredImage.data.attributes.formats, "xlarge")
        : undefined,
      imageAlt: metaTitle || title,
      noIndex: false,
      title: hasYuLifeSecondaryTitle ? metaTitle : `${metaTitle || title} | YuLife`,
      url: slug,
    };

    let html = post;
    html = giveBlockQuotesCurlyQuotationMarks(html);
    html = makeAnchorsOpenNewTab(html);

    return (
      <>
        <Meta locale={locale} {...metadata} />
        <div>
          <TitleContainer>
            <H1 pt="3rem" pb="1.5rem" align="center" lineheight="4rem">
              {title}
            </H1>
            {isCookiePolicy && (
              <ThemeContext.Consumer>
                {(props) => {
                  return (
                    <StyledP1
                      onClick={() => {
                        props.openCookieModal?.();
                      }}
                    >
                      <Cookie width="2rem" height="2rem" />
                      {cookiesLegalPage.link}
                      <ArrowContainer>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={24}>
                          <path
                            d="m14.7 11.4-4.4-4.2a.8.8 0 0 0-1.3.6v8.4a.8.8 0 0 0 1.3.6l4.4-4.2a.8.8 0 0 0 0-1.2Z"
                            fill="#5c5757"
                          />
                        </svg>
                      </ArrowContainer>
                    </StyledP1>
                  );
                }}
              </ThemeContext.Consumer>
            )}
          </TitleContainer>

          <BodyContainer>
            <Body dangerouslySetInnerHTML={{ __html: html }} />
          </BodyContainer>
        </div>
      </>
    );
  }
};

export default LegalPage;

const StyledP1 = styled.button`
  font-family: ${fonts.body};
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  color: ${color.B1100};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0 auto 1.5rem;
  border: none;
  background: none;
  padding: 0.5rem;

  &:hover {
    color: ${color.P100};
    cursor: pointer;

    path {
      fill: ${color.P100};
    }
  }
`;

const TitleContainer = styled.div`
  max-width: 46rem;
  padding: 0 2rem;
  margin: 0 auto;
`;

const ArrowContainer = styled.span`
  display: inline-block;
  transform: translate(-0.4rem, 0.3rem);
`;

const BodyContainer = styled.div`
  padding: 0 2rem;
  margin: 0 auto 10rem auto;
  max-width: 50rem;

  @media only screen and (max-width: 50rem) {
    margin-bottom: 5rem;
  }

  @media only screen and (max-width: 30rem) {
    padding: 0;
    margin-bottom: 0;
  }
`;

const Body = styled.div`
  padding: 0 2rem;
  background: #fff;
  border: 0.125rem solid #d9d9d7;
  box-shadow: 0.5rem 0.5rem 0 rgba(160, 160, 155, 0.24);
  border-radius: 0.5rem;
  padding: 1rem 2rem;

  @media only screen and (max-width: 65rem) {
    padding: 2rem;
  }

  @media only screen and (max-width: 30rem) {
    padding: 5%;
    padding-bottom: 4rem;
    border-radius: 0;
    border-width: 0.125rem 0;
  }

  animation: fadein 250ms;
  @keyframes fadein {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  p,
  li {
    position: relative;
    padding: 1rem 0;
    font-style: normal;
    font-weight: normal;
    font-size: 1.25rem;
    line-height: 2rem;
    letter-spacing: 0.075rem;
    color: #5c5757;
    font-weight: 400;
    text-decoration: inherit;

    @media only screen and (max-width: 65rem) {
      padding-top: 0;
    }

    a {
      color: ${themeColor.P100};

      &:hover {
        text-decoration: underline;
      }
    }
  }

  em {
    font-weight: 400;
    font-style: italic;
  }

  strong {
    font-weight: bold;
  }

  strong em,
  em strong {
    font-weight: bold;
    font-style: italic;
  }

  blockquote,
  blockquote > p,
  blockquote > p:first-child:first-letter,
  blockquote a {
    padding: 0;
    border: none;
    float: right;
    width: 300px;
    margin: 12px 0px 12px 36px;
    z-index: 2;

    @media only screen and (max-width: 30rem) {
      float: left;
      margin: 12px 0px 36px 0px;
      width: 100%;
    }

    position: relative;
    top: 5px;
    letter-spacing: 0;
    font-family: ${fonts.heading};
    font-size: 32px;
    line-height: 40px;
    color: #956aff;
  }

  blockquote a {
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    letter-spacing: 0;
    padding: 2rem 0 1rem 0;
  }

  h1,
  h2,
  h3 {
    color: #640038;
    font-family: ${fonts.heading};
    font-size: 2.5rem;
    -webkit-font-smoothing: auto;
    -moz-osx-font-smoothing: auto;

    span {
      font-family: ${fonts.heading};
    }
  }

  h1 {
    font-size: 3rem;

    @media only screen and (max-width: 30rem) {
      font-size: 2.5rem;
    }
  }

  h2 {
    font-size: 2.5rem;
  }

  h3 {
    font-size: 2rem;

    @media only screen and (max-width: 30rem) {
      font-size: 1.5rem;
    }
  }

  figure {
    margin: 0;
  }

  img {
    margin: 1.5rem auto;
  }

  iframe {
    margin: 0 auto;
    text-align: center;
    display: flex;
    justify-content: center;
  }

  figcaption {
    margin: -0.25rem 0 0 0;
    font-style: normal;
    font-weight: normal;
    font-size: 1rem;
    line-height: 1.5rem;
    letter-spacing: 0.075rem;
    color: #00c0f3;
    padding: 1rem 1.5rem;
    background: #e3f7fc;
    border-radius: 0 0 0.5rem 0.5rem;

    a {
      text-decoration: underline;
    }
  }

  ul,
  ol {
    list-style: none;
    margin-top: 0;

    @media only screen and (max-width: 65rem) {
      margin-top: 1rem;
    }
  }

  ol {
    counter-reset: custom-counter calc(var(--start) - 1);
  }

  ol li {
    counter-increment: custom-counter;
  }

  ol li::before {
    content: counter(custom-counter);
    color: #e30d76;
    font-weight: bold;

    position: absolute;
    left: -2.5rem;
    top: 0;

    transform: translateY(1rem);
  }

  ul {
    margin-left: 2.5rem;
  }

  ul li::before {
    content: "●";
    color: #e30d76;
    font-size: 0.85rem;

    position: absolute;
    left: -2.25rem;
    top: 0;

    transform: translateY(0.9rem);
  }

  li > p {
    margin: 0;
    padding: 0;
  }

  li > p:first-child:first-letter {
    float: none;
    font-size: inherit;
    color: inherit;
    font-family: inherit;
    padding: 0;
  }

  img {
    width: 100%;
  }
`;
